import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  UserCircleIcon, Cog6ToothIcon, RectangleStackIcon, DocumentTextIcon,
  HeartIcon, UsersIcon, BellIcon, ArrowRightOnRectangleIcon, AdjustmentsHorizontalIcon, FolderOpenIcon, UserGroupIcon, CalendarDaysIcon, Squares2X2Icon,
  ChatBubbleLeftEllipsisIcon, RocketLaunchIcon, HomeModernIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../context/AuthContext'
import { adresLogo } from '../api/account'
import { jestPolska } from '../kraj'
import useObservedSync from '../hooks/useObservedSync'
import { useTranslation } from 'react-i18next'
import { OknoZgloszenia } from './FormularzZgloszenia'
import { useSamouczek } from '../context/SamouczekContext'

// Preferencje doboru PIERWSZE, przed ustawieniami konta: to jedyna pozycja
// z tej listy, do której wraca się regularnie (marki, z których faktycznie
// składamy zestawy), a do niedawna była zakładką w środku ustawień.
const POZYCJE = [
  // Na samej górze (decyzja właściciela 2026-10-09): firma ma tu panel klientów (CRM, w nim teczki PME2),
  // osoba prywatna swoją teczkę PME2.
  // Tylko w Polsce: karta klienta prowadzi do polskich narzędzi (zgłoszenie do OSD, teczka PME2).
  // Pulpit firmy (2026-10-11): kalendarz, kolejne kroki, oferty, mapa montaży; strona startowa konta firmowego.
  { label: 'konto.pulpit', to: '/pulpit', Icon: Squares2X2Icon, firmowe: true, tylkoPolska: true },
  { label: 'konto.klienci', to: '/klienci', Icon: UserGroupIcon, firmowe: true, tylkoPolska: true },
  // Kalendarz firmy (2026-10-11): ten sam panel „Klienci”, od razu w widoku kalendarza.
  { label: 'konto.kalendarz', to: '/klienci?widok=kalendarz', Icon: CalendarDaysIcon, firmowe: true, tylkoPolska: true },
  // Teczka instalacji (uwagi przeglądu G3, 2026-10-25): Projekt 3D, licznik i rozdzielnica; dołącza się do zlecenia.
  { label: 'konto.moja_teczka', to: '/teczka', Icon: HomeModernIcon, prywatne: true, tylkoPolska: true },
  { label: 'konto.teczka_pme2', to: '/teczka-pme2', Icon: FolderOpenIcon, prywatne: true, tylkoPolska: true },
  { label: 'konto.preferencje', to: '/preferencje', Icon: AdjustmentsHorizontalIcon },
  // Tylko konto firmowe: konfiguracja dokumentu, ktory idzie do klienta koncowego
  // (marze, VAT, logo, chowanie rozbicia cen). Osobie prywatnej nie mowi nic.
  { label: 'konto.projekt', to: '/konto?sekcja=projekt', Icon: DocumentTextIcon, firmowe: true },
  // Zespół firmy (konta zespołowe, 2026-10-11): tylko konto z podpiętą firmą.
  { label: 'konto.zespol', to: '/konto?sekcja=zespol', Icon: UsersIcon, firmowe: true },
  { label: 'konto.powiadomienia', to: '/konto?sekcja=powiadomienia', Icon: BellIcon, firmowe: true },
  { label: 'konto.ustawienia', to: '/konto', Icon: Cog6ToothIcon },
  { label: 'konto.zestawy', to: '/konto', Icon: RectangleStackIcon },
  { label: 'konto.obserwowane', to: '/obserwowane', Icon: HeartIcon },
]

const UserMenu = () => {
  const { t } = useTranslation('wspolne')
  const { isAuthenticated, user, loading, logout, firmowe, profil } = useAuth()
  const [logoBlad, setLogoBlad] = useState(false)
  const navigate = useNavigate()
  const [otwarte, setOtwarte] = useState(false)
  const [zgloszenie, setZgloszenie] = useState(false)
  const samouczek = useSamouczek()
  const ref = useRef(null)

  // Przeniesienie obserwowanych z przeglądarki na konto — raz po zalogowaniu.
  useObservedSync(isAuthenticated, user?.email)

  // Zamykanie kliknięciem poza menu i klawiszem Escape. Bez tego menu zostaje
  // otwarte po przejściu na inną stronę i wygląda na zawieszone.
  useEffect(() => {
    if (!otwarte) return undefined
    const pozaMenu = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarte(false) }
    const escape = (e) => { if (e.key === 'Escape') setOtwarte(false) }
    document.addEventListener('mousedown', pozaMenu)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', pozaMenu)
      document.removeEventListener('keydown', escape)
    }
  }, [otwarte])

  // Dopóki nie wiemy, kto jest zalogowany, nie renderujemy nic — inaczej przy
  // każdym wejściu migałoby „Zaloguj się" u osoby zalogowanej.
  if (loading) return null

  if (!isAuthenticated) {
    return (
      /* Półka pod przyciskiem jak przy „Zostań partnerem" (Navbar.NavCta):
         ta sama mechanika (relative na opakowaniu, wysuw spod guzika).
         `min-w-full` przypina ją do SZEROKOŚCI PRZYCISKU — półka zaczyna się
         dokładnie tam, gdzie guzik, i nie podchodzi pod przełącznik motywu obok.
         Konsekwencja: napis nie mieści się w jednej linii, więc zamiast go
         skracać (i tracić „60 sekund", czyli całą obietnicę) łamiemy go na dwie
         wyśrodkowane linie — blok pod guzikiem wygląda wtedy jak jego podstawka. */
      /* Poniżej `md` (telefon, 2026-10-23) sama ikona bez półki: pasek dzieli tam miejsce z „Ogłoś na Rynku”
         i wyszukiwarką, a nazwę niesie `aria-label` i `title`. */
      <span className="relative inline-flex">
        <span
          aria-hidden="true"
          className="hidden md:block absolute top-full left-0 -mt-2 z-0 min-w-full pointer-events-none
                     px-1.5 pt-2.5 pb-0.5 rounded-b-xl text-center
                     bg-yellow-300 border border-t-0 border-yellow-400 shadow-sm
                     text-[9px] leading-[1.1] font-bold text-black"
        >
          {t('konto.rejestracja_60')}
        </span>
        {/* Przycisk na kolorze, nie szary tekst: to jedno z dwóch wejść dla nowych
            użytkowników (obok „Zostań partnerem") i ma je być widać. Podpis
            widoczny ZAWSZE — inaczej poniżej 1400 px zostawała sama ikona, a półka
            szerokości ikony nie pomieściłaby żadnego sensownego napisu. */}
        <button
          type="button"
          onClick={() => navigate('/logowanie')}
          title={t('konto.zaloguj')}
          aria-label={t('konto.zaloguj_aria')}
          // Na telefonie jasna ikona, żeby nie zlewała się z niebieskim „Ogłoś na Rynku” tuż obok.
          className="relative z-10 flex items-center gap-2 px-2 md:px-3 py-2 rounded-xl text-sm font-semibold
                     text-blue-700 bg-blue-50 hover:bg-blue-100
                     md:text-white md:bg-blue-600 md:hover:bg-blue-500 md:active:bg-blue-700 transition-colors
                     md:shadow-sm md:shadow-blue-600/25"
        >
          <UserCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span className="hidden md:inline whitespace-nowrap">{t('konto.zaloguj')}</span>
        </button>
      </span>
    )
  }

  const nazwa = user?.display || (user?.email ? user.email.split('@')[0] : t('konto.moje_konto'))
  // Logo firmy z ustawień „VoltValue Projekt” zamiast litery (prośba właściciela 2026-10-09).
  const logo = firmowe && profil?.logo_wersja && !logoBlad ? adresLogo(profil.logo_wersja) : null

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOtwarte((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={otwarte}
        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition-colors ${
          otwarte ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
        }`}
      >
        {logo ? (
          <span className="flex h-8 max-w-[7rem] flex-shrink-0 items-center rounded-md bg-white px-1 ring-1 ring-gray-200">
            <img src={logo} alt={profil?.company?.name || nazwa} onError={() => setLogoBlad(true)}
                 className="max-h-7 w-auto max-w-full object-contain" />
          </span>
        ) : (
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600
                           text-white text-xs font-bold flex-shrink-0">
            {nazwa.slice(0, 1).toUpperCase()}
          </span>
        )}
        {!logo && <span className="hidden pasek:inline whitespace-nowrap max-w-[120px] truncate">{nazwa}</span>}
      </button>

      {otwarte && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-60 bg-white rounded-xl border border-gray-100
                     shadow-lg overflow-hidden z-30"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs text-gray-400">{t('konto.zalogowany_jako')}</p>
            <p className="text-sm font-medium text-gray-800 truncate">{user?.email}</p>
          </div>

          {POZYCJE.filter((p) => (!p.firmowe || firmowe) && (!p.prywatne || !firmowe) && (!p.tylkoPolska || jestPolska) && (!p.zFirma || profil?.company)).map(({ label, to, Icon }) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              onClick={() => { setOtwarte(false); navigate(to) }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700
                         hover:bg-gray-50 transition-colors text-left"
            >
              <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
              {t(label)}
            </button>
          ))}

          {/* Samouczek „Pierwsze kroki” (2026-10-13): zawsze można do niego wrócić, z postępem, dopóki nie jest skończony. */}
          {samouczek.dostepny && firmowe && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOtwarte(false); samouczek.otworz() }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700
                         hover:bg-gray-50 transition-colors text-left"
            >
              <RocketLaunchIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="flex-1">{t('konto.pierwsze_kroki')}</span>
              {!samouczek.stan.koniec && samouczek.stan.wszystkich > 0 && (
                <span className="text-xs font-semibold tabular-nums text-blue-700">{samouczek.stan.zrobione}/{samouczek.stan.wszystkich}</span>
              )}
            </button>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={async () => { setOtwarte(false); await logout(); navigate('/') }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700
                       hover:bg-red-50 hover:text-red-600 transition-colors text-left
                       border-t border-gray-100"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4 flex-shrink-0" />
            {t('konto.wyloguj')}
          </button>

          {/* Ostatnia pozycja (prośba właściciela 2026-10-13): ten sam formularz co w dymku w lewym dolnym rogu. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOtwarte(false); setZgloszenie(true) }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700
                       hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
          >
            <ChatBubbleLeftEllipsisIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
            {t('zglos.przycisk')}
          </button>
        </div>
      )}

      {zgloszenie && <OknoZgloszenia onZamknij={() => setZgloszenie(false)} />}
    </div>
  )
}

export default UserMenu
